import { qfRestGet } from './qfClient';

/**
 * Market-wide earnings calendar over a date range.
 *
 * Served by QuantForge `GET /v1/calendar/earnings/` (live 2026-08-06, QF #702).
 * Integration reference: `docs/plans/2026-08-06-qf-calendar-endpoints-LIVE.md`.
 *
 * ### Ranking is server-side
 *
 * The default `sort_by=marketcap` ranks largest first with nulls last, and the
 * default `universe=optionable` restricts to names with listed options — the
 * tradeable set, and the one where market cap is actually populated (98.5% of
 * rows, against 16.5% for `all`). Both defaults are what this page wants, so
 * neither parameter is sent.
 *
 * ⚠️ `universe=all` is mostly noise: 64% of its rows are dot-suffixed foreign
 * listings (`000035.SZ`, `0016.HK`) with no market cap, so they cannot be
 * ranked and would crowd out real names.
 *
 * ### ⚠️ `time: "unknown"` is the MAJORITY case
 *
 * The reporting slot is empty for ~64% of rows upstream. A layout that only
 * renders `bmo` and `amc` strands most of a day's companies, so the page gives
 * `unknown` an explicit treatment rather than hiding those rows or guessing a
 * slot for them.
 *
 * ### Fields that do not exist yet
 *
 * `expectedMove`, `expectedMovePct`, `reaction`, `preClose`, `isSp500` and
 * `countryCode` have no upstream source and never appear in the response today.
 * They are kept on the interface because the shape is right and they may be
 * filled later; any UI depending on them simply renders nothing.
 *
 * ### When a row settles — a past date is not final
 *
 *  • Before the report: `streetEst`, `marketCap`, `name`, `sector`
 *  • Evening of the report: `actualEps`
 *  • Later, sometimes never: `revenue`, `revenueEst`
 *
 * `revenue` is genuinely never published for some companies, so a past day with
 * EPS and no revenue is a normal terminal state rather than a gap to chase.
 *
 * ### Coverage
 *
 * Forward to T+120, rolling and refreshed daily, so the runway does not shrink
 * as the year advances. Backward coverage is limited — reads are filtered to
 * recently-refreshed rows, so dates more than ~30 days in the past return few
 * or no rows. This is a forward-looking calendar.
 */

/** Whether a company reports before the open or after the close. */
export type ReportTime = 'bmo' | 'amc' | 'unknown';

export interface EarningsEvent {
  /** ISO date, YYYY-MM-DD, in EASTERN terms. */
  date: string;
  ticker: string;
  /** Company name, for the tooltip. */
  name?: string;
  /** ⚠️ `unknown` for ~64% of rows — the majority, not an edge case. */
  time: ReportTime;
  /** Market cap in dollars. Decides which names survive a crowded cell. */
  marketCap?: number;

  /* ── the print itself ──────────────────────────────────────────────────
     Absent until the relevant moment — see "When a row settles" above.
     Optional fields are OMITTED from the response, never sent as null. */

  /** Consensus EPS. */
  streetEst?: number;
  /** Reported EPS. Only after the print. */
  actualEps?: number;
  /** Reported revenue. Lags the EPS print, and sometimes never arrives. */
  revenue?: number;
  revenueEst?: number;
  sector?: string;

  /* ── not served today ──────────────────────────────────────────────────
     No upstream source as of 2026-08-06. Kept because the shape is right. */

  /** Options-implied move, as a FRACTION (0.112 = 11.2%). */
  expectedMovePct?: number;
  /** The same move in dollars. */
  expectedMove?: number;
  /** What the stock did, as a FRACTION. */
  reaction?: number;
  /** Close before the report, for context alongside the move. */
  preClose?: number;
  isSp500?: boolean;
  /** ISO-2 country code. */
  countryCode?: string;
}

/**
 * Every earnings report between two dates, inclusive. Eastern calendar dates.
 *
 * Rows come back ranked by market cap, largest first — do not re-sort by cap on
 * the client.
 *
 * The response is `{events: [...]}` with no envelope, so unlike the economic
 * calendar there is no null-envelope case to check: failures surface as an
 * ordinary non-2xx, which `qfRestGet` throws on. Letting that reach React Query
 * matters — an empty calendar and an unreachable one render identically, and
 * the difference decides whether a quiet week is real.
 */
export async function fetchEarningsRange(
  fromIso: string,
  toIso: string
): Promise<EarningsEvent[]> {
  const body = await qfRestGet<{ events?: EarningsEvent[] }>('/v1/calendar/earnings/', {
    from: fromIso,
    to: toIso,
  });
  return body.events ?? [];
}
